import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

import { Autoplay } from "swiper/modules";
import SwiperCore from "swiper";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import AllResults from "./SearchTabs/AllResults";
import MDAs from "./SearchTabs/MDAs";
import { ICON_INDIVIDUALS } from "common/constants";
import Individuals from "./SearchTabs/Individuals";
import Payments from "./SearchTabs/Payments";
import Requests from "./SearchTabs/Requests";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { setSearchValue } from "slices/search/reducer";
import ReportingAgencies from "./SearchTabs/ReportingAgencies";
import BulkSearch from "./BulkSearch";

// Tab ids stay numeric because reactstrap keys NavLink/TabPane off them; the URL
// carries a readable slug instead, so /due-diligence?tab=payments is shareable.
const TAB_SLUGS: Record<string, string> = {
  "1": "all",
  "2": "individuals",
  "3": "mdas",
  "4": "payments",
  "5": "agencies",
  "6": "requests",
};

const DEFAULT_TAB = "1";

const tabFromSlug = (slug: string | null): string => {
  if (!slug) return DEFAULT_TAB;
  const bySlug = Object.entries(TAB_SLUGS).find(([, value]) => value === slug);
  if (bySlug) return bySlug[0];
  // Tolerate the old numeric form so links already in circulation still land.
  return TAB_SLUGS[slug] ? slug : DEFAULT_TAB;
};

const SearchResults = () => {
  SwiperCore.use([Autoplay]);

  const dispatch: any = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  //Tab
  const [activeTab, setActiveTab] = useState(() =>
    tabFromSlug(searchParams.get("tab"))
  );
  const toggleTab = (tab: React.SetStateAction<string>) => {
    if (activeTab !== tab) {
      const next = tab?.toString();
      setActiveTab(next);
      setSearchParams(
        (params) => {
          const newParams = new URLSearchParams(params);
          // The default tab needs no param at all.
          if (next === DEFAULT_TAB) newParams.delete("tab");
          else newParams.set("tab", TAB_SLUGS[next] ?? next);
          return newParams;
        },
        { replace: true }
      );
    }
  };
  useEffect(() => {
    const fromUrl = tabFromSlug(searchParams.get("tab"));
    if (activeTab !== fromUrl) setActiveTab(fromUrl);
  }, [activeTab, searchParams]);

  const selectProperties = createSelector(
    (state: any) => state.Search,
    (state) => ({
      globalSearchValue: state.globalSearchValue,
    })
  );
  const { globalSearchValue: searchValue } = useSelector(selectProperties);

  useEffect(() => {
    if (searchValue === "") {
      dispatch(setSearchValue(searchParams.get("search") || ""));
    }
  }, [dispatch, searchParams, searchValue]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb
            title="Search"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="">
                    <Col lg={12}>
                      <Row className="g-2">
                        <Col sm={12}>
                          <div className="position-relative">
                            <Input
                              type="text"
                              className="form-control form-control-lg bg-light border-light"
                              style={{
                                paddingRight: "120px",
                              }}
                              placeholder="Search here..."
                              value={searchValue}
                              onChange={(e) => {
                                dispatch(setSearchValue(e.target.value));

                                setSearchParams(
                                  (params) => {
                                    const newParams = new URLSearchParams(
                                      params
                                    );
                                    newParams.set("search", e.target.value);
                                    return newParams;
                                  },
                                  { replace: true }
                                );
                              }}
                            />

                            <div className="position-absolute end-0 top-0 d-flex align-items-center ">
                              <Link
                                to="#"
                                className="btn btn-link link-muted btn-lg"
                                onClick={() => {
                                  if (searchValue.length > 0) {
                                    dispatch(setSearchValue(""));
                                    setSearchParams(
                                      (params) => {
                                        const newParams = new URLSearchParams(
                                          params
                                        );
                                        newParams.delete("search");
                                        return newParams;
                                      },
                                      { replace: true }
                                    );
                                  }
                                }}
                              >
                                <i
                                  className={
                                    searchValue?.length > 0
                                      ? "mdi mdi-close-circle fs-18 text-muted"
                                      : "ri-search-line text-muted"
                                  }
                                ></i>
                              </Link>
                              <span className="text-muted fs-18 ">|</span>

                              <BulkSearch />
                            </div>
                          </div>
                        </Col>
                        {/* <Col sm={2}>
                          <DownloadSearchReport />
                        </Col> */}
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col lg={12}>
              <Card>
                <div>
                  <Nav className="nav-tabs nav-tabs-custom" role="tablist">
                    <NavItem>
                      <NavLink
                        href="#"
                        className={classnames({
                          active: activeTab === "1",
                          "py-3": true,
                        })}
                        onClick={() => {
                          toggleTab("1");
                        }}
                      >
                        <i className="ri-search-2-line text-muted align-bottom me-1"></i>{" "}
                        All Results
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href="#"
                        className={classnames({
                          active: activeTab === "3",
                          "py-3": true,
                        })}
                        onClick={() => {
                          toggleTab("3");
                        }}
                      >
                        <i
                          className={`ri-bank-line text-muted align-bottom me-1`}
                        ></i>{" "}
                        MDAs
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href="#"
                        className={classnames({
                          active: activeTab === "2",
                          "py-3": true,
                        })}
                        onClick={() => {
                          toggleTab("2");
                        }}
                      >
                        <i
                          className={`${ICON_INDIVIDUALS} text-muted align-bottom me-1`}
                        ></i>{" "}
                        Individuals
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        href="#"
                        className={classnames({
                          active: activeTab === "4",
                          "py-3": true,
                        })}
                        onClick={() => {
                          toggleTab("4");
                        }}
                      >
                        <i
                          className={`ri-exchange-dollar-fill text-muted align-bottom me-1`}
                        ></i>{" "}
                        Payments
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href="#"
                        className={classnames({
                          active: activeTab === "5",
                          "py-3": true,
                        })}
                        onClick={() => {
                          toggleTab("5");
                        }}
                      >
                        <i
                          className={`ri-earth-line text-muted align-bottom me-1`}
                        ></i>{" "}
                        Reporting Agencies
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href="#"
                        className={classnames({
                          active: activeTab === "6",
                          "py-3": true,
                        })}
                        onClick={() => {
                          toggleTab("6");
                        }}
                      >
                        <i
                          className={`ri-send-plane-2-line text-muted align-bottom me-1`}
                        ></i>{" "}
                        Requests
                      </NavLink>
                    </NavItem>
                    <li className="nav-item ms-auto">
                      {/* // Bulk search button */}
                    </li>
                  </Nav>
                </div>
                <CardBody className="p-4">
                  <TabContent activeTab={activeTab} className="text-muted">
                    <TabPane tabId="1">
                      <AllResults
                        activeTab={activeTab}
                        searchValue={searchValue}
                      />
                    </TabPane>
                    <TabPane tabId="2">
                      <Individuals
                        activeTab={activeTab}
                        searchValue={searchValue}
                      />
                    </TabPane>
                    <TabPane tabId="3">
                      <MDAs activeTab={activeTab} searchValue={searchValue} />
                    </TabPane>
                    <TabPane tabId="4">
                      <Payments
                        activeTab={activeTab}
                        searchValue={searchValue}
                      />
                    </TabPane>
                    <TabPane tabId="5">
                      <ReportingAgencies
                        activeTab={activeTab}
                        searchValue={searchValue}
                      />
                    </TabPane>
                    <TabPane tabId="6">
                      <Requests
                        activeTab={activeTab}
                        searchValue={searchValue}
                      />
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SearchResults;
